import { ISourcingRun } from '../types'

/**
 * The run's displayed status, live-derived from `counts` (BRAND-9582, QA
 * item 14). A single approve/reject/undo patches a run's `counts` locally
 * (see `agentSourcedCreatorActionSuccess` in `store/agentSourcedCreators/
 * reducer.ts`) but never its `status` — that only arrives on a fresh fetch,
 * which single decisions deliberately don't trigger — so the raw `status`
 * can go stale after the last pending creator is decided, or after an undo
 * reopens a completed run. Ted's ruling: derive it client-side from `counts`
 * instead of adding a refetch.
 *
 * Pure function of `status` + `counts`, recomputed every render — nothing to
 * revert, so the undo direction needs no separate logic. Only moves between
 * the two completed-import statuses, and only when the run holds creators —
 * an all-zero completed run is `isCompletedWithNoCreators`'s territory
 * (utils/sourcingRunEmptyResult.ts), not this function's; the two must never
 * both claim the same run.
 *
 * The two literals below are matched directly rather than via
 * `effectiveSelectedRun.ts`'s `COMPLETED_IMPORT_STATUSES` Set (MINOR 7,
 * BRAND-9582 review) — that Set grows for an unrelated purpose
 * (`getDefaultRunOrdinal`), and coupling to it would silently relabel a
 * future status added there for that other reason.
 */
const RECOMPUTABLE_STATUSES = new Set(['ready_for_vibe_check', 'vibe_check_completed'])

export const effectiveSourcingRunStatus = (run: ISourcingRun): string => {
  if (!RECOMPUTABLE_STATUSES.has(run.status)) return run.status

  const { pending, approved, rejected, ready_for_shortlist: readyForShortlist } = run.counts
  const hasCreators = pending + approved + rejected + readyForShortlist > 0
  if (!hasCreators) return run.status

  return pending === 0 ? 'vibe_check_completed' : 'ready_for_vibe_check'
}
